// Overview cockpit: per-inverter status grid (8 mini instrument widgets) +
// intra-fleet spread band over the daylight window (min–max + median).
import i18n from '@/i18n';
import { useMemo } from 'react';
import { Cpu } from 'lucide-react';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  ComposedChart, Area, Line, Label,
} from 'recharts';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { useChartTheme } from '@/components/base/chartTheme';
import { useInverterLatest, useInverterDay, type InvLatest } from '@/scada/useInverters';

export const median = (xs: number[]) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
export const devColor = (pct: number | null, med: number | null, offline?: boolean) => {
  if (offline) return scada.state.offline;
  if (pct == null || med == null) return scada.textMuted;
  const d = pct - med;
  if (d >= -3) return scada.state.ok;
  if (d >= -10) return scada.state.warning;
  return scada.state.alarm;
};

// Loading placeholder that keeps the card height stable (no layout shift).
function Skeleton({ height }: { height: number }) {
  return <div className="animate-pulse" style={{ height, background: scada.panel2, borderRadius: 8 }} />;
}

// Шкала з поділками — той самий візуальний патерн, що й «Інвертори» в EMS
// Box (засічки, кожна 6-та мажорна, маркер поточного значення), лише
// ВЕРТИКАЛЬНА: тут картка компактна й горизонтальна смуга на всю ширину не
// лишає місця для трьох колонок цифр (DC / AC / % номіналу), а вертикальна
// стає ще одною колонкою поруч із ними.
function VerticalScale({ value, max, color = scada.state.ok, height = 58 }: { value: number | null; max: number; color?: string; height?: number }) {
  const frac = value == null || max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  const N = 24;
  // Прямокутний трикутник із поділок: вертикальний катет — ліва вісь, вістря
  // внизу, основа вгорі. Довжина поділки росте лінійно з висотою, тож площа
  // підсвіченої частини сама показує продуктивність інвертора — око ловить
  // різницю ще до того, як прочитає «% номіналу».
  const W_MIN = 2, W_MAX = 16;
  const wAt = (f: number) => W_MIN + f * (W_MAX - W_MIN);
  const ticks: any[] = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    const major = i % 6 === 0;
    const lit = f <= frac + 1e-6;
    ticks.push(<div key={i} style={{
      position: 'absolute', bottom: `${f * 100}%`, left: 0,
      width: wAt(f) + (major ? 2 : 0), height: major ? 1.6 : 1,
      background: lit ? color : scada.borderStrong, opacity: lit ? 0.95 : 0.4,
      transform: 'translateY(50%)', borderRadius: 1,
      transition: 'background .5s, opacity .5s',
    }} />);
  }
  return (
    <div style={{ position: 'relative', width: W_MAX + 6, height, flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 1, background: scada.borderStrong, opacity: 0.4 }} />
      {ticks}
      {value != null && <div style={{
        position: 'absolute', bottom: `${frac * 100}%`, left: -2, transform: 'translateY(50%)',
        width: wAt(frac) + 5, height: 2.4, background: color, borderRadius: 2,
        boxShadow: `0 0 6px ${color}`, transition: 'bottom .6s cubic-bezier(.4,0,.2,1), width .6s cubic-bezier(.4,0,.2,1)',
      }} />}
    </div>
  );
}

/**
 * Картка одного інвертора — спільна для «Огляду» (сітка стану) і вкладки
 * «Інвертори» (клікабельна сітка з деталями): чіп-іконка + #N у шапці,
 * DC/AC/% номіналу в тілі, вертикальна шкала праворуч. Винесено сюди, щоб
 * обидва місця показували ІДЕНТИЧНУ картку, а не дві схожі реалізації.
 * `selected` — рамка приймає колір вибору (як і в попередній heatmap-сітці
 * вкладки «Інвертори»), не впливає на решту стилю.
 */
export function InverterCard({ inv, med, selected }: { inv: InvLatest; med: number | null; selected?: boolean }) {
  const c = devColor(inv.percentOfNominal, med, inv.status === 'offline');
  const pct = Math.max(0, Math.min(100, inv.percentOfNominal ?? 0));
  const dev = med != null && inv.percentOfNominal != null ? inv.percentOfNominal - med : null;
  const statusLabel = inv.status === 'offline' ? i18n.t('ak_scada2_offline') : inv.synthetic ? i18n.t('ak_scada2_estimate') : i18n.t('ak_scada2_normal');
  return (
    <div style={{ background: scada.panel2, border: `1px solid ${selected ? scada.accent : scada.border}`, borderRadius: 10, padding: '12px 13px', display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center" style={{ gap: 8 }}>
          <span style={{ width: 27, height: 27, borderRadius: 8, background: scada.panel, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Cpu size={15} strokeWidth={1.9} style={{ color: scada.text }} />
          </span>
          <span className="font-display" style={{ fontWeight: 600, fontSize: 15, color: scada.text }}>#{String(inv.unitId).padStart(2, '0')}</span>
        </span>
        {/* Лишається тiльки лампочка-iндикатор без пiдпису - колiр уже несе
            стан, текст поруч iз назвою рядка дублював очевидне. */}
        <span title={statusLabel} style={{ width: 6, height: 6, borderRadius: 999, background: c, display: 'inline-block', flexShrink: 0 }} />
      </div>
      {/* Шкала злiва, вища (76px пiд весь блок цифр) i вирiвняна по верхньому
          краю з "DC" (align-items:flex-start замiсть center). */}
      <div className="flex items-start" style={{ gap: 10 }}>
        <VerticalScale value={pct} max={100} color={c} height={76} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-baseline justify-between">
            <span style={{ fontSize: 10, color: scada.textMuted }}>DC</span>
            <span className="font-display" style={{ fontSize: 12.5, fontWeight: 600, color: scada.text }}>
              {(inv.ppvKw ?? 0).toFixed(1)}<span style={{ fontSize: 9, color: scada.textMuted, fontWeight: 400 }}> {i18n.t('ak_scada2_kw')}</span>
            </span>
          </div>
          <div className="flex items-baseline justify-between" style={{ marginTop: 5 }}>
            <span style={{ fontSize: 10, color: scada.textMuted }}>AC</span>
            <span className="font-display" style={{ fontSize: 19, fontWeight: 700, color: scada.text, lineHeight: 1 }}>
              {(inv.pacKw ?? 0).toFixed(1)}<span style={{ fontSize: 10, color: scada.textMuted, fontWeight: 400 }}> {i18n.t('ak_scada2_kw')}</span>
            </span>
          </div>
          <div className="flex items-baseline justify-between" style={{ marginTop: 5 }}>
            <span style={{ fontSize: 10, color: scada.textMuted }}>{i18n.t('ak_scada2_nom')}</span>
            <span className="font-display" style={{ fontSize: 13, fontWeight: 600, color: c }}>
              {inv.percentOfNominal != null ? `${inv.percentOfNominal}%` : '—'}
            </span>
          </div>
          {dev != null && (
            <div style={{ fontSize: 9.5, color: dev >= -3 ? scada.textMuted : c, textAlign: 'right', marginTop: 2 }}>
              {i18n.t('ak_scada2_vs_med', { s: dev > 0 ? '+' : '', d: dev.toFixed(0) })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Per-inverter status grid: 8 mini instrument widgets ──────────────────────
export function InverterStrip({ assetId }: { assetId?: string }) {
  const { data, meta, err } = useInverterLatest(assetId);
  const mode = meta?.mode;
  const perInverter = mode === 'per_inverter';
  // Показуємо ЛИШЕ реальні пристрої. У режимі `aggregate` контролер віддає
  // тільки сумарну потужність, а бекенд домальовує стільки рядків, скільки
  // інверторів за паспортом (expectedInverters), рівномірно поділивши суму, і
  // мітить їх synthetic:true. Такі картки не відповідають жодному пристрою —
  // у сітці їм не місце. Фільтр стоїть і для per_inverter: якщо бекенд колись
  // добиватиме ряд до паспортної кількості, вигадані картки не проскочать.
  const real = useMemo(() => (data ?? []).filter((d) => !d.synthetic), [data]);
  const med = useMemo(() => median(real.map((d) => d.percentOfNominal).filter((x): x is number => x != null)), [real]);
  const working = real.filter((d) => d.status === 'ok').length;

  return (
    <SectionCard
      title={i18n.t('ak_scada2_inverter_technical_status')}
      subtitle={perInverter ? i18n.t('ak_scada2_each_inverter_power_of_nominal_deviati') : i18n.t('ak_scada2_station_inverter_fleet_status')}
      right={real.length > 0 && med != null ? <span style={{ color: scada.textMuted, fontSize: 11 }}>{i18n.t('ak_scada2_running_median', { w: working, n: real.length, m: med.toFixed(0) })}</span> : undefined}
    >
      {!data && !err && <Skeleton height={110} />}
      {(err || mode === 'none') && !data && (
        <span style={{ color: scada.textMuted, fontSize: 12 }}>{i18n.t('ak_scada2_no_inverter_data_for_this_station')}</span>
      )}

      {/* Aggregate (SMA): group banner above a synthesized fleet grid */}
      {data && mode === 'aggregate' && meta?.groupName && (
        <div className="flex items-center gap-2 flex-wrap" style={{ background: `${scada.state.curtailed}12`, border: `1px solid ${scada.state.curtailed}33`, borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          <i className="ri-server-line" style={{ color: scada.state.curtailed }} />
          <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{meta.groupName}</span>
          <span style={{ color: scada.textMuted, fontSize: 12 }}>{i18n.t('ak_scada2_running_total_mw', { a: meta.availableInverters ?? '—', e: meta.expectedInverters ?? '—', p: meta.plantPacKw != null ? (meta.plantPacKw / 1000).toFixed(2) : '—' })}</span>
          <span style={{ color: scada.textMuted, fontSize: 11, marginLeft: 'auto' }}>{i18n.t('ak_scada2_the_controller_sends_totals_only_no_pe')}</span>
        </div>
      )}

      {/* Сітка інверторів: ФІКСОВАНА кількість колонок, максимум 8 на desktop.
          Було repeat(auto-fill, minmax(132px, 1fr)) — auto-fill набиває стільки
          колонок, скільки влізе, тож на широкому моніторі виходило 9-11 карток
          у ряду. Фіксований крок ще й прибирає розтягування: при 3 інверторах
          картки лишаються тієї ж ширини, що й при 64, а не стають на пів
          екрана. Колонки — minmax(0,1fr) (так їх задає Tailwind), тож ширина
          однакова й переповнення немає. */}
      {/* Сітка тільки за наявності реальних пристроїв. */}
      {real.length > 0 && (
      <div className="grid gap-2.5 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
        {real.map((inv) => <InverterCard key={inv.unitId} inv={inv} med={med} />)}
      </div>
      )}
    </SectionCard>
  );
}

// ── Intra-fleet spread over the day: min–max band + median (daylight only) ───
export function StationDayChart({ assetId }: { assetId?: string }) {
  const ct = useChartTheme();
  const { data: latest, meta } = useInverterLatest(assetId);
  const { series, err } = useInverterDay(assetId);
  const nominalKw = latest?.[0]?.nominalKw ?? null;
  const mode = meta?.mode;

  // Station total power over the day (kW) — used for aggregate stations.
  const powerDay = useMemo(() => {
    if (!series) return [];
    const byTime = new Map<string, number>();
    for (const s of series) for (const p of s.points) {
      if (p.pac == null) continue;
      byTime.set(p.t, (byTime.get(p.t) ?? 0) + p.pac);
    }
    const all = [...byTime.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([t, kw]) => ({ t, kw: Math.round(kw) }));
    const lo = all.findIndex((p) => p.kw > 1);
    const hi = all.length - 1 - [...all].reverse().findIndex((p) => p.kw > 1);
    return lo === -1 ? [] : all.slice(lo, hi + 1);
  }, [series]);

  const band = useMemo(() => {
    if (!series || !nominalKw) return [];
    const byTime = new Map<string, number[]>();
    for (const s of series) for (const p of s.points) {
      if (p.pac == null) continue;
      const pct = (p.pac / nominalKw) * 100;
      if (!byTime.has(p.t)) byTime.set(p.t, []);
      byTime.get(p.t)!.push(pct);
    }
    const all = [...byTime.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([t, arr]) => {
      const mn = Math.min(...arr), mx = Math.max(...arr);
      return { t, min: mn, range: mx - mn, max: mx, med: median(arr) ?? 0, spread: mx - mn };
    });
    const lo = all.findIndex((p) => p.max > 1);
    const hi = all.length - 1 - [...all].reverse().findIndex((p) => p.max > 1);
    return lo === -1 ? [] : all.slice(lo, hi + 1);
  }, [series, nominalKw]);

  const loading = !series && !err;
  const aggregate = mode === 'aggregate';
  const maxSpread = band.length ? Math.max(...band.map((b) => b.spread)) : 0;
  const hasContent = aggregate ? powerDay.length > 0 : band.length > 0;

  return (
    <SectionCard
      title={aggregate ? i18n.t('ak_scada2_station_power_per_day') : i18n.t('ak_scada2_inverter_uniformity_per_day')}
      subtitle={aggregate
        ? i18n.t('ak_scada2_total_station_power_aggregated_meterin')
        : i18n.t('ak_scada2_green_line_the_typical_average_inverte')}
      right={!aggregate && band.length ? <span style={{ color: maxSpread > 15 ? scada.state.warning : scada.textMuted, fontSize: 11 }}>{i18n.t('ak_scada2_max_spread', { n: maxSpread.toFixed(0) })}</span> : undefined}
    >
      {loading && <Skeleton height={230} />}
      {!loading && !hasContent && <span style={{ color: scada.textMuted, fontSize: 12 }}>{i18n.t('ak_scada2_no_data_for_today')}</span>}

      {/* Aggregate: station total power (kW) area chart */}
      {aggregate && powerDay.length > 0 && (
        <div style={{ width: '100%', height: 230 }}>
          <ResponsiveContainer>
            <ComposedChart data={powerDay} margin={{ top: 8, right: 12, bottom: 18, left: 4 }}>
              <defs>
                <linearGradient id="stnpg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ct.series.ok} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={ct.series.ok} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid {...ct.gridProps} />
              <XAxis dataKey="t" {...ct.axis} minTickGap={36}>
                <Label value={i18n.t('ak_scada2_time_of_day')} position="bottom" offset={4} fill={ct.muted} fontSize={11} />
              </XAxis>
              <YAxis {...ct.axis} tickFormatter={(v) => `${v}`} width={48}>
                <Label value={i18n.t('ak_scada2_kw')} angle={-90} position="insideLeft" fill={ct.muted} fontSize={11} style={{ textAnchor: 'middle' }} />
              </YAxis>
              <Tooltip {...ct.tooltip} cursor={ct.cursorLine}
                formatter={(v: any) => [`${v} ${i18n.t('ak_scada2_kw')}`, i18n.t('ak_scada2_power')]} />
              <Area type="monotone" dataKey="kw" stroke={ct.series.ok} strokeWidth={2.5} fill="url(#stnpg)" isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {!aggregate && band.length > 0 && (
      <div style={{ width: '100%', height: 230 }}>
        <ResponsiveContainer>
          <ComposedChart data={band} margin={{ top: 8, right: 12, bottom: 18, left: 4 }}>
            <CartesianGrid {...ct.gridProps} />
            <XAxis dataKey="t" {...ct.axis} minTickGap={36}>
              <Label value={i18n.t('ak_scada2_time_of_day')} position="bottom" offset={4} fill={ct.muted} fontSize={11} />
            </XAxis>
            <YAxis {...ct.axis} tickFormatter={(v) => `${v}%`} width={42}>
              <Label value={i18n.t('ak_scada2_of_nominal_2')} angle={-90} position="insideLeft" fill={ct.muted} fontSize={11} style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip
              {...ct.tooltip} cursor={ct.cursorLine}
              formatter={(_v: any, _n: any, p: any) => {
                const d = p.payload;
                return [i18n.t('ak_scada2_weakest_typical_strongest', { a: d.min.toFixed(0), b: d.med.toFixed(0), c: d.max.toFixed(0) }), i18n.t('ak_scada2_inverters')];
              }} />
            <Area dataKey="min" stackId="band" stroke="none" fill="transparent" isAnimationActive={false} />
            <Area dataKey="range" stackId="band" stroke="none" fill={ct.series.s2} fillOpacity={0.18} isAnimationActive={false} name={i18n.t('ak_scada2_spread')} />
            <Line dataKey="med" stroke={ct.series.ok} name={i18n.t('ak_scada2_median')} {...ct.line} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      )}
    </SectionCard>
  );
}
